import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import api from "../api/api";
import Header from "../components/Header";
import LeftSidebar from "../components/sidebar";

const formatDateTime = (datetime, timezone) => {
  if (!datetime) return "N/A";
  try {
    const str = String(datetime);
    const time = toHHMM(str);
    const isoMatch = str.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const yyyy = isoMatch[1];
      const mm = isoMatch[2];
      const dd = isoMatch[3];
      return `${mm}/${dd}/${yyyy} ${time}`.trim();
    }
    const textMatch = str.match(/\b(\d{2})\s+([A-Za-z]{3})\s+(\d{4})\b/);
    if (textMatch) {
      const dd = textMatch[1];
      const mon = textMatch[2].toLowerCase();
      const yyyy = textMatch[3];
      const monthMap = {
        jan: "01",
        feb: "02",
        mar: "03",
        apr: "04",
        may: "05",
        jun: "06",
        jul: "07",
        aug: "08",
        sep: "09",
        oct: "10",
        nov: "11",
        dec: "12",
      };
      const mm = monthMap[mon] || "01";
      return `${mm}/${dd}/${yyyy} ${time}`.trim();
    }
    return time || str;
  } catch {
    return String(datetime);
  }
};

const formatDateTimeWithTz = (datetime, timezone) => {
  if (!datetime) return "N/A";
  try {
    const date = new Date(datetime);
    return date
      .toLocaleString("en-US", {
        timeZone: timezone || "UTC",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .replace(",", "");
  } catch {
    return String(datetime);
  }
};

const formatDate = (datetime) => {
  if (!datetime) return "N/A";
  try {
    const date = new Date(datetime);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return String(datetime);
  }
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  return value;
};

const toDisplayDay = (dateStr) => {
  if (!dateStr) return "--";
  try {
    const d = new Date(`${dateStr}T00:00:00`);
    const day = String(d.getDate()).padStart(2, "0");
    const mon = d.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
    return `${day} ${mon}`;
  } catch {
    return "--";
  }
};

const formatTimeDayMon = (datetime) => {
  if (!datetime) return "";
  try {
    const str = String(datetime);
    const date = new Date(str);
    if (Number.isNaN(date.getTime())) return str;
    const time = toHHMM(str);
    const day = String(date.getDate()).padStart(2, "0");
    const mon = date.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
    return `${time} ${day}${mon}`;
  } catch {
    return String(datetime);
  }
};

const toHHMM = (datetime) => {
  if (!datetime) return "";
  try {
    const str = String(datetime);
    // Prefer extracting time from ISO datetime strings without conversion.
    if (str.includes("T") || /^\d{4}-\d{2}-\d{2}/.test(str)) {
      const isoMatch = str.match(/T(\d{2}:\d{2})/);
      if (isoMatch) return isoMatch[1];
      const spaceMatch = str.match(/\b(\d{2}:\d{2})\b/);
      if (spaceMatch) return spaceMatch[1];
    }
    // Keep the original time portion as provided (no conversion).
    const timeMatch = str.match(/\b\d{2}:\d{2}\b/);
    if (timeMatch) return timeMatch[0];
    // Handle compact HHMM or HHMMSS tokens (e.g., 0759 or 075900)
    const compactMatch = str.match(/(\d{4})(\d{2})?\b/);
    if (compactMatch) {
      const hhmm = compactMatch[1];
      return `${hhmm.slice(0, 2)}:${hhmm.slice(2)}`;
    }
    if (str.includes("T")) {
      const timePart = str.split("T")[1] || "";
      return timePart.slice(0, 5);
    }
    if (str.includes(" ")) {
      const timePart = str.split(" ")[1] || "";
      return timePart.slice(0, 5);
    }
    return str;
  } catch {
    return "";
  }
};

const cleanDescription = (desc) => {
  if (!desc) return "";
  let cleaned = desc;
  cleaned = cleaned.replace(/DESCRIPTION:/gi, "");
  cleaned = cleaned.replace(/This data imported on.*?\)/gi, "");
  cleaned = cleaned.replace(/Pilot/gi, "");
  cleaned = cleaned.replace(/Rotation Rest/gi, "");
  cleaned = cleaned.replace(/\n\s*\n/g, "\n").trim();
  return cleaned;
};

const normalizeDescriptionForParsing = (description = "") => {
  if (!description) return "";

  let text = String(description).replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  // Microsoft body content can come as HTML.
  if (text.includes("<") && text.includes(">")) {
    text = text
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<\/div>/gi, "\n")
      .replace(/<\/li>/gi, "\n")
      .replace(/<[^>]+>/g, "");
  }

  // Decode common entities and spacing variants.
  text = text
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\u00a0/g, " ")
    .replace(/\u2013/g, "-")
    .replace(/\u2014/g, "-");

  // Collapse excessive blank lines but keep row structure.
  text = text.replace(/\n[ \t]*\n+/g, "\n\n");
  return text.trim();
};

const parseLegacySegments = (description = "") => {
  const normalized = normalizeDescriptionForParsing(description);
  const lines = normalized.split("\n").map((l) => l.trim()).filter(Boolean);
  const days = [];
  let currentDay = null;

  lines.forEach((line) => {
    const rptMatch = line.match(/^Rpt-\s*(\d+)\s*(\d{2}[A-Z]{3})/);
    if (rptMatch) {
      const rawReport = rptMatch[1];
      const formattedReport =
        rawReport.length === 4 ? `${rawReport.slice(0, 2)}:${rawReport.slice(2)}` : rawReport;
      const rawDay = rptMatch[2];
      const formattedDay = rawDay.length === 5 ? `${rawDay.slice(0, 2)} ${rawDay.slice(2)}` : rawDay;
      currentDay = {
        reportTime: formattedReport,
        day: formattedDay,
        flights: [],
        layover: null,
        hotel: null,
      };
      days.push(currentDay);
      return;
    }

    const flightMatch = line.match(
      /^(?:D\s+)?([A-Z]{2,3}\d+)\s+([A-Z]{3})\s*-\s*([A-Z]{3})\s+(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})\s+([A-Z0-9]{2,4})/i
    );
    if (flightMatch && currentDay) {
      currentDay.flights.push({
        flightNo: flightMatch[1],
        from: flightMatch[2],
        to: flightMatch[3],
        departure: flightMatch[4],
        arrival: flightMatch[5],
        aircraft: flightMatch[6],
      });
      return;
    }

    const layoverMatch = line.match(/^LAYOVER\s+(\d{2}:\d{2})\/([A-Z]{3})/);
    if (layoverMatch && currentDay) {
      currentDay.layover = {
        duration: layoverMatch[1],
        location: layoverMatch[2],
      };
      return;
    }

    const hotelMatch = line.match(/^(.+?):\s+(\d{3}-\d{3}-\d{4})/);
    if (hotelMatch && currentDay) {
      currentDay.hotel = {
        name: hotelMatch[1],
        phone: hotelMatch[2],
      };
    }
  });

  return days;
};

const buildStructuredDays = (event) => {
  const rotation = event?.rotation;
  if (!rotation || !rotation.duty_days?.length) return [];

  return rotation.duty_days.map((d) => ({
    reportTime: toHHMM(
      d.report_at,
      (d.flight_segments && d.flight_segments[0] && d.flight_segments[0].departure_timezone) ||
        event.start_timezone
    ),
    day: toDisplayDay(d.duty_date),
    flights: (d.flight_segments || []).map((s) => ({
      flightNo: s.flight_number,
      from: s.departure_airport,
      to: s.arrival_airport,
      departure: toHHMM(s.departure_at),
      arrival: toHHMM(s.arrival_at),
      depOffset: s.departure_offset_note,
      arrOffset: s.arrival_offset_note,
      aircraft: s.aircraft,
      tail: s.tail_number,
      eqpShip: s.aircraft && s.tail_number ? `Eqp/Ship- ${s.aircraft}/${s.tail_number}` : null,
      gateDep: s.gate_dep,
      gateArr: s.gate_arr,
      blockTime: s.block_time,
    })),
    layover:
      d.layovers && d.layovers.length
        ? {
            duration: d.layovers[0].duration,
            location: d.layovers[0].airport_code,
          }
        : null,
    hotel:
      d.layovers && d.layovers.length && d.layovers[0].hotel_name
        ? {
            name: d.layovers[0].hotel_name,
            phone: d.layovers[0].hotel_phone,
          }
        : null,
  }));
};

const buildDaysFromSegments = (event) => {
  const rotation = event?.rotation;
  const segments = rotation?.segments || [];
  if (!segments.length) return [];

  const grouped = new Map();
  segments.forEach((seg) => {
    if (!seg.departure_at) return;
    const dateKey = seg.departure_at.slice(0, 10);
    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, {
        day: toDisplayDay(dateKey),
        reportTime: null,
        flights: [],
        layover: null,
        hotel: null,
      });
    }
    const day = grouped.get(dateKey);
    day.flights.push({
      flightNo: seg.flight_number,
      from: seg.departure_airport,
      to: seg.arrival_airport,
      departure: toHHMM(seg.departure_at),
      arrival: toHHMM(seg.arrival_at),
      depOffset: seg.departure_offset_note,
      arrOffset: seg.arrival_offset_note,
      aircraft: seg.aircraft,
      tail: seg.tail_number,
      eqpShip: seg.aircraft && seg.tail_number ? `Eqp/Ship- ${seg.aircraft}/${seg.tail_number}` : null,
      gateDep: seg.gate_dep,
      gateArr: seg.gate_arr,
      blockTime: seg.block_time,
    });
    if (seg.layover && seg.layover.airport_code) {
      day.layover = {
        duration: seg.layover.duration,
        location: seg.layover.airport_code,
      };
      if (seg.layover.hotel_name) {
        day.hotel = {
          name: seg.layover.hotel_name,
          phone: seg.layover.hotel_phone,
        };
      }
    }
  });

  const days = Array.from(grouped.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([, value]) => value);

  if (rotation?.report_time && days.length) {
    days[0].reportTime = toHHMM(rotation.report_time);
  }

  days.forEach((day) => {
    if (!day.reportTime && day.flights.length) {
      day.reportTime = day.flights[0].departure || "--";
    }
  });

  return days;
};

const calculateDutyDays = (start, end) => {
  if (!start || !end) return 0;
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 0;
  const oneDay = 24 * 60 * 60 * 1000;
  let days = Math.floor((endDate - startDate) / oneDay) + 1;

  const isMidnight = (d) =>
    d.getHours() === 0 &&
    d.getMinutes() === 0 &&
    d.getSeconds() === 0 &&
    d.getMilliseconds() === 0;

  const sameClockTime =
    startDate.getHours() === endDate.getHours() &&
    startDate.getMinutes() === endDate.getMinutes() &&
    startDate.getSeconds() === endDate.getSeconds() &&
    startDate.getMilliseconds() === endDate.getMilliseconds();

  // For all-day style ranges (00:00 -> next day 00:00), treat end as exclusive.
  if (days > 1 && sameClockTime && isMidnight(startDate) && isMidnight(endDate)) {
    days -= 1;
  }

  return Math.max(1, days);
};

const extractCities = (days) => {
  const airports = new Set();
  days.forEach((day) => {
    day.flights.forEach((f) => {
      airports.add(f.from);
      airports.add(f.to);
    });
    if (day.layover?.location) airports.add(day.layover.location);
  });
  return airports.size ? [...airports].join(", ") : "--";
};

const RotationDetailStructured = () => {
  const { eventUid  } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { routeLoading } = useOutletContext() || {};

  useEffect(() => {
    const fetchEventDetail = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get(`/event/${eventUid}`);
        setEvent(res.data || null);
      } catch (err) {
        setError(err?.response?.data?.error || "Failed to load rotation details");
      } finally {
        setLoading(false);
      }
    };
    if (eventUid) fetchEventDetail();
  }, [eventUid]);

  const structuredDays = useMemo(() => buildStructuredDays(event), [event]);
  const segmentDays = useMemo(() => buildDaysFromSegments(event), [event]);
  const legacyDays = useMemo(
    () => parseLegacySegments(event?.parse_meta?.clean_description || event?.description || ""),
    [event?.parse_meta?.clean_description, event?.description]
  );
  const displayDays = segmentDays.length
    ? segmentDays
    : structuredDays.length
    ? structuredDays
    : legacyDays;

  const countOvernights = (days) => days.filter((d) => d.layover).length;

  if (routeLoading || loading) return null;

  if (error || !event) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">{error || "Rotation not found"}</div>
        <button className="btn btn-secondary" onClick={() => navigate("/dashboard")}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <main className="dashboard-screen">
      <div className="container-fluid">
        <div className="row gx-5">
          <div className="col-lg-3 left-sidebar-main">
            <LeftSidebar />
          </div>
          <div className="col-lg-9">
            <Header />
            <div className="row contact-section">
              <div className="col-sm-12">
                <h1 className="event-detail-title">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{ background: "none", border: "none", padding: 0, marginRight: "8px" }}
                    aria-label="Back"
                  >
                    <img src="/icons/logout.svg" alt="Back" />
                  </button>
                  Event Details
                </h1>
                <div className="event-inner">
                  <div className="dash-section3 mb-5 rotation-title">
                    <h3 className="title-inner mb-5">Rotation Summary&nbsp;&nbsp;|&nbsp;&nbsp;<span className="eventSummary">{event.summary || "Untitled Rotation"}</span></h3>
                         {/* <div className="row mb-4">
                      <div className="col-md-6">
                        <h5 className="title-inner"></h5>
                   <span className="title_small">Calendar: {event.calendar_name || "Unknown Calendar"}</span>
                      </div>
                    </div> */}

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <span className="title_small">Start Date & Time</span>
                        <div className="subtitle4">
                          {event.rotation
                            ? formatDateTime(event.rotation?.report_time, event.start_timezone)
                            : formatDateTimeWithTz(event.start, event.start_timezone)} ({event.start_timezone})
                        </div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <span className="title_small">End Date & Time</span>
                        <div className="subtitle4">
                          {event.rotation
                            ? formatDateTime(event.rotation?.release_time, event.end_timezone)
                            : formatDateTimeWithTz(event.end, event.end_timezone)} ({event.end_timezone})
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <span className="title_small">Report Time</span>
                        <div className="subtitle4">{displayValue(toHHMM(event.rotation?.report_time))}</div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <span className="title_small">Release Time</span>
                        <div className="subtitle4">{displayValue(toHHMM(event.rotation?.release_time))}</div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <span className="title_small">Cities Involved</span>
                        <div className="subtitle4">{extractCities(displayDays)}</div>
                      </div>
                      <div className="col-md-3 mb-3">
                        <span className="title_small">Duration (Days)</span>
                        <div className="subtitle4">
                          {displayValue(event.rotation?.days ?? calculateDutyDays(event.start, event.end))}
                        </div>
                      </div>
                      <div className="col-md-3 mb-3">
                        <span className="title_small">Overnights</span>
                        <div className="subtitle4">{displayValue(countOvernights(displayDays))}</div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3" />
                      <div className="col-md-3 mb-3">
                        <span className="title_small">Credit hours</span>
                        <div className="subtitle4">{displayValue(event.rotation?.credit_hours)}</div>
                      </div>
                      <div className="col-md-3 mb-3">
                        <span className="title_small">TAFB</span>
                        <div className="subtitle4">{displayValue(event.rotation?.tafb_hours)}</div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3" />
                      <div className="col-md-6 mb-3">
                        <span className="title_small">Layovers</span>
                        <div className="subtitle4">{displayValue(event.rotation?.layover_cities)}</div>
                      </div>
                    </div>

                    {event.location && (
                      <div className="row">
                        <div className="col-12 mb-3">
                          <span className="title_small">Location</span>
                          <div>{event.location}</div>
                        </div>
                      </div>
                    )}

                  </div>

                  <div className="event-inner2">
                    {displayDays.length > 0 ? (
                      <>
                        <h5 className="title-inner mb-4">Flight Segments</h5>

                        <div className="dash-section3 rotation-title days-sec">
                          {displayDays.map((day, dayIndex) => (
                            <div key={dayIndex} className="div1">
                              <div className="row mb-3 align-items-center">
                                <div className="col-md-8">
                                    <h3 className="title-inner">
                                      DAY {dayIndex + 1} - {day.day} {new Date(event.start).getFullYear()}
                                    </h3>
                                </div>
                              </div>
                              

                              

                              {day.flights.map((f, flightIndex) => (
                                <div key={flightIndex} className="mb-4">
                                  <div className="row mb-2">
                                    <div className="col-md-12">
                                      <h5 className="title-inner2">
                                        {f.flightNo}: {f.from} <i className="bi bi-arrow-right"></i> {f.to}
                                      </h5>
                                    </div>
                                  </div>

                                  <div className="row">
                                    <div className="col-md-4 mb-3 col-4">
                                      <span className="title_small">Departure:</span>
                                      <div className="subtitle4">
                                        {displayValue(f.departure)}
                                        {f.depOffset ? ` ${f.depOffset}` : ""}
                                      </div>
                                    </div>

                                    <div className="col-md-4 mb-3 col-4">
                                      <span className="title_small">Arrival:</span>
                                      <div className="subtitle4">
                                        {displayValue(f.arrival)}
                                        {f.arrOffset ? ` ${f.arrOffset}` : ""}
                                      </div>
                                    </div>

                                    <div className="col-md-4 mb-3 col-4">
                                      <span className="title_small">Aircraft:</span>
                                      <div className="subtitle4">{displayValue(f.aircraft)}</div>
                                    </div>

                                  </div>

                                  <div className="row">
                                    <div className="col-md-4 mb-3 col-4">
                                      <span className="title_small">Gate Dep:</span>
                                      <div className="subtitle4">{displayValue(f.gateDep)}</div>
                                    </div>

                                    <div className="col-md-4 mb-3 col-4">
                                      <span className="title_small">Gate Arr:</span>
                                      <div className="subtitle4">{displayValue(f.gateArr)}</div>
                                    </div>

                                    <div className="col-md-4 mb-3 col-4">
                                      <span className="title_small">Block:</span>
                                      <div className="subtitle4">{displayValue(f.blockTime)}</div>
                                    </div>

                                    <div className="col-md-4 mb-3 col-4">
                                      <span className="title_small">Eqp/Ship:</span>
                                      <div className="subtitle4">{displayValue(f.eqpShip)}</div>
                                    </div>
                                  </div>
                                </div>
                              ))}

                              {day.layover && (
                                <>
                                  <div className="row mt-4 mb-2">
                                    <div className="col-md-12">
                                      <h5 className="title-inner">Layover:</h5>
                                    </div>
                                  </div>

                                  <div className="row">
                                    <div className="col-md-6 mb-3">
                                      <span className="title_small">Location:</span>
                                      <div className="subtitle4">{displayValue(day.layover.location)}</div>
                                    </div>

                                    <div className="col-md-6 mb-3">
                                      <span className="title_small">Time:</span>
                                      <div className="subtitle4">{displayValue(day.layover.duration)}</div>
                                    </div>
                                  </div>
                                </>
                              )}

                              {day.hotel && (
                                <div className="row">
                                  <div className="col-md-6 mb-3">
                                    <span className="title_small">Hotel:</span>
                                    <div className="subtitle4">{displayValue(day.hotel.name)}</div>
                                  </div>

                                  <div className="col-md-6 mb-3">
                                    <span className="title_small">Hotel Phone:</span>
                                     <div className="subtitle4">
                                       {day.hotel.phone ? (
                                         <a href={`tel:${day.hotel.phone}`} style={{ color: "#F3F4F7" }}>
                                           {day.hotel.phone}
                                         </a>
                                       ) : (
                                         "--"
                                       )}
                                     </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <h5 className="title-inner mb-4">Description</h5>
                        <div className="dash-section3 mb-0">
                          <div className="subtitle4" style={{ whiteSpace: "pre-line" }}>
                            {cleanDescription(
                              event?.parse_meta?.clean_description || event?.description
                            ) || "No details available."}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* <div className="dash-section3 mt-5">
                    <div className="event-additional-info">
                      <strong>Calendar:</strong> {event.calendar_name || "Unknown"} <br />
                      <strong>Created:</strong> {formatDate(event.created_at) || "N/A"}
                    </div>
                  </div> */}
                  {/* Debug Details removed */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default RotationDetailStructured;
